/**
 * Social network share URL templates and link builder.
 *
 * Replaces the (unmaintained, alpha, partly Vue-2-broken) `vue-social-sharing` package.
 *
 * Placeholder substitutions in templates:
 *   @u  = url
 *   @t  = title
 *   @d  = description
 *   @q  = quote
 *   @h  = hashtags
 *   @m  = media
 *   @tu = twitterUser
 *
 * SSR-safe: no `window` / `document` / `navigator` access at module load time.
 * Any environment-specific behavior (like the iOS SMS quirk) is gated inside
 * `buildShareLink` and only resolved when called.
 */

export const SOCIAL_NETWORKS = Object.freeze({
    baidu: 'http://cang.baidu.com/do/add?iu=@u&it=@t',
    buffer: 'https://bufferapp.com/add?text=@t&url=@u',
    email: 'mailto:?subject=@t&body=@u%0D%0A@d',
    evernote: 'https://www.evernote.com/clip.action?url=@u&title=@t',
    facebook: 'https://www.facebook.com/sharer/sharer.php?u=@u&title=@t&description=@d&quote=@q&hashtag=@h',
    flipboard: 'https://share.flipboard.com/bookmarklet/popout?v=2&url=@u&title=@t',
    hackernews: 'https://news.ycombinator.com/submitlink?u=@u&t=@t',
    imgur: 'https://imgur.com',
    instapaper: 'http://www.instapaper.com/edit?url=@u&title=@t&description=@d',
    line: 'http://line.me/R/msg/text/?@t%0D%0A@u%0D%0A@d',
    linkedin: 'https://www.linkedin.com/shareArticle?url=@u',
    messenger: 'fb-messenger://share/?link=@u',
    odnoklassniki: 'https://connect.ok.ru/dk?st.cmd=WidgetSharePreview&st.shareUrl=@u&st.comments=@t',
    pinterest: 'https://pinterest.com/pin/create/button/?url=@u&media=@m&description=@t',
    pocket: 'https://getpocket.com/save?url=@u&title=@t',
    quora: 'https://www.quora.com/share?url=@u&title=@t',
    reddit: 'https://www.reddit.com/submit?url=@u&title=@t',
    skype: 'https://web.skype.com/share?url=@t%0D%0A@u%0D%0A@d',
    sms: 'sms:?body=@t%0D%0A@u%0D%0A@d',
    stumbleupon: 'https://www.stumbleupon.com/submit?url=@u&title=@t',
    telegram: 'https://t.me/share/url?url=@u&text=@t%0D%0A@d',
    tumblr: 'https://www.tumblr.com/share/link?url=@u&name=@t&description=@d',
    twitter: 'https://twitter.com/intent/tweet?text=@t&url=@u&hashtags=@h@tu',
    viber: 'viber://forward?text=@t%0D%0A@u%0D%0A@d',
    vk: 'https://vk.com/share.php?url=@u&title=@t&description=@d&image=@m&noparse=true',
    weibo: 'http://service.weibo.com/share/share.php?url=@u&title=@t&pic=@m',
    whatsapp: 'https://api.whatsapp.com/send?text=@t%0D%0A@u%0D%0A@d',
    wordpress: 'https://wordpress.com/press-this.php?u=@u&t=@t&s=@d&i=@m',
    xing: 'https://www.xing.com/social/share/spi?op=share&url=@u&title=@t',
    yammer: 'https://www.yammer.com/messages/new?login=true&status=@t%0D%0A@u%0D%0A@d',
});

/**
 * Encode hashtags for the given network. Facebook uses a single `%23`-prefixed
 * tag; everything else uses the raw comma-separated list.
 */
function encodeHashtags(network, hashtags) {
    if (!hashtags) return '';
    if (network === 'facebook') {
        return '%23' + hashtags.split(',')[0];
    }
    return hashtags;
}

/**
 * Build a fully-resolved share URL for the given network and content.
 *
 * @param {string} network - Network key (must exist in SOCIAL_NETWORKS).
 * @param {object} content - Content fields:
 *   {string} url, {string} title, {string} description,
 *   {string} quote, {string} hashtags, {string} twitterUser, {string} media.
 *   All optional except as required by the chosen network's template.
 * @param {object} [env] - Optional environment override (mostly for tests).
 *   {string} env.userAgent - Defaults to `navigator.userAgent` at call time.
 * @returns {string} Resolved URL.
 * @throws {Error} If `network` is unknown.
 */
export function buildShareLink(network, content = {}, env = {}) {
    const key = String(network).toLowerCase();

    if (!Object.prototype.hasOwnProperty.call(SOCIAL_NETWORKS, key)) {
        throw new Error(`Unknown social network: ${network}`);
    }

    let template = SOCIAL_NETWORKS[key];

    // iOS SMS quirk: the SMS share URL needs `&` instead of `?` after `:` on iOS.
    // Resolved lazily so this util is SSR-safe (no `navigator` at module load).
    if (key === 'sms') {
        const userAgent = (env.userAgent
            ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
        ).toLowerCase();

        if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
            template = template.replace(':?', ':&');
        }
    }

    const {
        url = '',
        title = '',
        description = '',
        quote = '',
        hashtags = '',
        twitterUser = '',
        media = '',
    } = content;

    // Twitter shouldn't include empty parameters (matches original library behavior).
    if (key === 'twitter') {
        if (!hashtags.length) template = template.replace('&hashtags=@h', '');
        if (!twitterUser.length) template = template.replace('@tu', '');
    }

    return template
        .replace(/@tu/g, '&via=' + encodeURIComponent(twitterUser))
        .replace(/@u/g, encodeURIComponent(url))
        .replace(/@t/g, encodeURIComponent(title))
        .replace(/@d/g, encodeURIComponent(description))
        .replace(/@q/g, encodeURIComponent(quote))
        .replace(/@h/g, encodeHashtags(key, hashtags))
        .replace(/@m/g, encodeURIComponent(media));
}

/**
 * Returns true if the given network's resolved URL uses HTTP(S) — meaning it
 * should open in a popup. Anything else (mailto:, sms:, fb-messenger:, viber:)
 * should open in the same/new tab without a popup.
 */
export function isHttpShareLink(link) {
    return typeof link === 'string' && link.startsWith('http');
}
